"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ResetPassword.css";
import logo from "../../assets/DHMS_logo.webp";
import Reset from "../../assets/Reset.webp";
import Toast from "../../Components/Modals/Toast/Toast";
import AuthService from "../../Services/authService";

const LoadingSpinner = () => (
  <div className="R-spinner">
    <div className="R-spinner-circle"></div>
  </div>
);

const CheckCircleIcon = ({ met }) =>
  met ? (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="9" cy="9" r="9" fill="#2a66b0" />
      <path d="M5 9.5l2.5 2.5 5.5-5.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="9" cy="9" r="8" stroke="#d1d5db" strokeWidth="1.5" fill="none" />
      <path d="M5 9.5l2.5 2.5 5.5-5.5" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

const PasswordReset = () => {
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });

  const navigate = useNavigate();

  const showToast = (message, type = "info") => setToast({ show: true, message, type });
  const hideToast = () => setToast({ show: false, message: "", type: "info" });

  const hasMinLength = (p) => p.length >= 8;
  const hasUppercase = (p) => /[A-Z]/.test(p);
  const hasNumber = (p) => /\d/.test(p);
  const hasSpecialChar = (p) => /[!@#$%^&*(),.?":{}|<>-]/.test(p);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      showToast("Please enter your email address", "error");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast("Please enter a valid email address", "error");
      return;
    }

    if (!newPassword) {
      showToast("Please enter a new password", "error");
      return;
    }

    if (!hasMinLength(newPassword)) {
      showToast("Password must be at least 8 characters long!", "error");
      return;
    }

    if (!hasUppercase(newPassword)) {
      showToast("Password must contain at least one uppercase letter!", "error");
      return;
    }

    if (!hasNumber(newPassword)) {
      showToast("Password must contain at least one number!", "error");
      return;
    }

    if (!hasSpecialChar(newPassword)) {
      showToast("Password must contain at least one special character!", "error");
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast("Passwords do not match!", "error");
      return;
    }

    setIsLoading(true);
    try {
      // PUT /family/setnewpasswordwithnotification
      await AuthService.resetPasswordWithNotification({
        emailaddress: email.trim(),
        newPassword,
      });
      setShowSuccessModal(true);
    } catch (error) {
      let message = "Failed to reset password. Please try again.";
      if (error.message.includes("Network error")) {
        message = "Network error. Please check your internet connection.";
      } else if (error.message.includes("No account")) {
        message = "No account found with this email address.";
      } else if (error.message.includes("Too many")) {
        message = "Too many requests. Please wait a few minutes before trying again.";
      } else if (error.message) {
        message = error.message;
      }
      showToast(message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="R-password-reset-container">
        <div className="R-content-wrapper">
          <div className="R-image-section">
            <div className="R-header-logo">
              <a href="/">
                <img
                  src={logo || "/placeholder.svg"}
                  alt="IT Service Desk Africa"
                  className="R-company-logo"
                />
              </a>
            </div>
            <div className="R-family-image-container">
              <img
                src={Reset || "/placeholder.svg"}
                alt="Happy African family using laptop together"
                className="R-family-image2"
              />
            </div>
          </div>

          <div className="R-form-section">
            <div className="R-form-container">
              <h1 className="R-form-title">Forgot Password?</h1>
              <p className="R-form-subtitle">
                Enter your email address and choose a new password to reset your account.
              </p>
              <form onSubmit={handleSubmit} className="R-reset-form">
                {/* Email */}
                <div className="R-input-group">
                  <label htmlFor="email" className="R-input-label">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="R-email-input"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>

                {/* New Password */}
                <div className="R-input-group">
                  <label htmlFor="newPassword" className="R-input-label">
                    New Password
                  </label>
                  <div className="R-password-input-container">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="newPassword"
                      className="R-password-input"
                      placeholder="Enter your new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onFocus={() => setIsPasswordFocused(true)}
                      onBlur={() => setIsPasswordFocused(false)}
                      disabled={isLoading}
                      required
                    />
                    <button
                      type="button"
                      className="R-password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? "🙈" : "👁️"}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="R-input-group">
                  <label htmlFor="confirmPassword" className="R-input-label">
                    Confirm Password
                  </label>
                  <div className="R-password-input-container">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      className="R-password-input"
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                    <button
                      type="button"
                      className="R-password-toggle-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? "🙈" : "👁️"}
                    </button>
                  </div>
                </div>

                {/* Password strength hints */}
                {isPasswordFocused && (
                  <div className="R-password-requirements">
                    <p className="R-requirements-title">Your password must include at least</p>
                    <div className="R-requirement-list">
                      <div className={`R-requirement-item${hasMinLength(newPassword) ? " R-met" : ""}`}>
                        <CheckCircleIcon met={hasMinLength(newPassword)} />
                        <span>Minimum of 8 characters</span>
                      </div>
                      <div className={`R-requirement-item${hasUppercase(newPassword) ? " R-met" : ""}`}>
                        <CheckCircleIcon met={hasUppercase(newPassword)} />
                        <span>One UPPERCASE letter</span>
                      </div>
                      <div className={`R-requirement-item${hasNumber(newPassword) ? " R-met" : ""}`}>
                        <CheckCircleIcon met={hasNumber(newPassword)} />
                        <span>one number</span>
                      </div>
                      <div className={`R-requirement-item${hasSpecialChar(newPassword) ? " R-met" : ""}`}>
                        <CheckCircleIcon met={hasSpecialChar(newPassword)} />
                        <span>One special character (e.g !"@#%^&?)</span>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="R-submit-button"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner />
                      Resetting Password...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </button>

                <p className="R-login-link">
                  Remember your password?{" "}
                  <span
                    className="R-login-link-text"
                    onClick={() => navigate("/login")}
                  >
                    Log in
                  </span>
                </p>
              </form>
            </div>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="R-modal-overlay" onClick={() => {}}>
            <div className="R-modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="R-modal-content">
                <div className="R-modal-icon">
                  <div className="R-message-bubble">
                    <div className="R-bubble-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <div className="R-notification-badge">✅</div>
                  </div>
                </div>
                <h2 className="R-modal-title">Password Reset Successful</h2>
                <p className="R-modal-text">
                  Your password has been successfully reset. You can now log in with your new password.
                </p>
                <div className="R-modal-actions">
                  <button
                    className="R-continue-btn"
                    onClick={() => navigate("/login")}
                  >
                    Go to Login
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
        duration={5000}
      />
    </>
  );
};

export default PasswordReset;
